/**
 * PlanManeuverButton component - Retro-styled button to trigger maneuver planning
 *
 * Features:
 * - Retro amber/orange theme styling
 * - Loading state with spinner
 * - Disabled state when no CDM selected
 * - Sends WebSocket message to trigger plan_maneuver tool
 * - Monospace font for retro aesthetic
 */

import { useState } from 'react'
import { Rocket, Loader2 } from 'lucide-react'

interface PlanManeuverButtonProps {
  cdmId: string | null
  onPlanManeuver: (cdmId: string) => void
  disabled?: boolean
  className?: string
}

function PlanManeuverButton({
  cdmId,
  onPlanManeuver,
  disabled = false,
  className = '',
}: PlanManeuverButtonProps) {
  const [isLoading, setIsLoading] = useState(false)

  const handleClick = () => {
    if (!cdmId || disabled || isLoading) return

    setIsLoading(true)

    // Send the planning request
    onPlanManeuver(cdmId)

    // Reset loading after a short delay (actual completion is handled by WebSocket)
    // This provides immediate feedback while the request is sent
    setTimeout(() => {
      setIsLoading(false)
    }, 1500)
  }

  const isDisabled = disabled || !cdmId || isLoading

  return (
    <button
      onClick={handleClick}
      disabled={isDisabled}
      className={`
        inline-flex items-center gap-2
        px-4 py-2
        font-mono text-sm font-medium
        border-2 rounded
        transition-all duration-200
        ${
          isDisabled
            ? 'border-dark-300 text-dark-400 bg-dark-100 cursor-not-allowed opacity-50'
            : `
              border-retro-border text-retro-text bg-transparent
              hover:bg-retro-border hover:text-retro-bg
              active:scale-95
            `
        }
        ${className}
      `}
      title={
        !cdmId
          ? 'Select a CDM to plan a maneuver'
          : isLoading
          ? 'Planning maneuver...'
          : `Plan collision avoidance maneuver for CDM ${cdmId}`
      }
    >
      {isLoading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <Rocket className="w-4 h-4" />
      )}
      <span>
        {isLoading ? 'PLANNING...' : 'PLAN MANEUVER'}
      </span>
    </button>
  )
}

export default PlanManeuverButton
